import { http } from './api'

// ── Types matching BriefingResponse / BriefingDetailResponse ──────────────

export interface BriefingItem {
  id: string
  company_name: string
  company_id: string
  tag_score: number | null
  trend_label: string | null          // surging | rising | stable | declining
  surge_velocity: number | null
  priority: string                    // p0 | p1 | p2
  status: string                      // generated | delivered | actioned | expired
  delivered_via: string | null
  delivered_at: string | null
  actioned_at: string | null
  created_at: string
}

export interface BriefingData {
  intent_summary?: Record<string, unknown>
  buying_committee?: Array<{ name?: string; title?: string; engagement?: string }>
  warm_paths?: Array<{ via?: string; strength?: string; note?: string }>
  call_insights?: {
    last_call_date?: string
    sentiment?: number
    buying_signals?: string[]
    unresolved_objections?: string[]
  }
  recommended_action?: string
  draft_outreach?: string
}

export interface NetworkBenchmarks {
  /** e.g. "12% above industry median" or descriptive label */
  headline?: string
  /** Discrete benchmark rows, e.g. {label:"Reply rate", value:"24%", median:"18%"} */
  rows?: Array<{ label: string; value: string; median?: string; delta?: string }>
  /** Number of tenants this benchmark is aggregated across */
  tenant_count?: number
  /** Free-form note from the aggregator */
  note?: string
}

export interface SimilarPattern {
  /** Short pattern label, e.g. "PLG mid-market evaluator" */
  label: string
  /** Win rate when this pattern matched */
  win_rate?: number
  /** Sample size */
  sample_size?: number
  /** What worked */
  what_worked?: string
}

export interface CompetitiveIntel {
  /** Competitor names mentioned in deals like this */
  competitors?: string[]
  /** Common talking points or positioning notes */
  positioning?: string
  /** Win/loss rate vs each competitor when known */
  vs?: Array<{ competitor: string; wins: number; losses: number }>
}

export interface BriefingDetail extends BriefingItem {
  briefing_markdown: string | null
  briefing_data: BriefingData | null
  outcome_notes: string | null
  alert_id: string | null
  assigned_rep_id: string | null
  network_benchmarks: NetworkBenchmarks | null
  similar_patterns: SimilarPattern[] | null
  competitive_intel: CompetitiveIntel | null
}

export interface BriefingListResponse {
  items: BriefingItem[]
  total: number
  page: number
  page_size: number
}

export interface ActionBriefingRequest {
  status: 'actioned' | 'expired'
  outcome_notes?: string
}

// ── API client ─────────────────────────────────────────────────────────────

export const dealBriefingApi = {
  list: (page = 1, pageSize = 20, status?: string) =>
    http<BriefingListResponse>('/deal-briefings/', {
      method: 'GET',
      query: { page, page_size: pageSize, ...(status ? { status } : {}) },
    }),

  get: (briefingId: string) =>
    http<BriefingDetail>(`/deal-briefings/${briefingId}`, { method: 'GET' }),

  action: (briefingId: string, body: ActionBriefingRequest) =>
    http<BriefingDetail>(`/deal-briefings/${briefingId}`, { method: 'PATCH', body }),

  regenerate: (briefingId: string) =>
    http<{ status: string; briefing_id: string }>(`/deal-briefings/${briefingId}/regenerate`, {
      method: 'POST',
    }),
}
